import { useCallback, useEffect, useRef, useState } from 'react';
import { padResultRequestId, submitPadResult, type PadResultScore } from '../lib/api/pad';

// ─────────────────────────────────────────────────────────────────────
// SENDING A RESULT FROM A TABLET ON HALL WI-FI (#185).
//
// The pad is bolted to a wall, the access point is at the other end of a sports
// hall, and the operator walks away the moment they have pressed send. So the
// submission cannot depend on anyone being there to retry it, and it must not
// be able to apply twice when the network comes back.
//
// WHAT IS OPTIMISTIC AND WHAT IS NOT. The score the operator entered is theirs
// and stays on screen while this works — blanking it mid-outage would be worse
// than useless. Nothing else is: the match, the table and every rating come
// back from the server, and until they do the pad says «Sender…» rather than
// pretending the result is in.
//
// RETRIES CARRY THE SAME DERIVED ID, so the server recognises its own work and
// answers with the court view instead of applying anything a second time. The
// id is recomputed from the match and the score rather than remembered, which
// is why a reload mid-outage is safe too.
// ─────────────────────────────────────────────────────────────────────

/** How long between attempts while the server cannot be reached. */
export const PAD_RESULT_RETRY_MS = 5_000;

/**
 * How long the pad SAYS a result went in, before it goes back to showing the
 * court.
 *
 * From the hall (#216): «it was faster to jump out of the match. A bit too
 * fast. Did my result go through, it just say reserved.» A result applies in a
 * couple of hundred milliseconds, and the only thing that changed on screen
 * was the match disappearing — which looks exactly like the match disappearing
 * for any other reason.
 *
 * The confirmation is not cosmetic: it is the only moment the person who
 * entered the score can tell a success from a silent failure. Six seconds is
 * long enough to read across a court and short enough that a wall-mounted pad
 * is not left showing it.
 */
export const PAD_RESULT_CONFIRM_MS = 6_000;

export interface PadPendingResult {
  matchId: string;
  score: PadResultScore;
  /** Derived, not generated — see padResultRequestId. */
  requestId: string;
  /** Attempts made so far, for the screen's «prøver igjen» wording. */
  attempts: number;
  /** Epoch ms of the first attempt, so the pad can say how long it has been. */
  since: number;
}

/** A result the SERVER applied — what the pad confirms, and for how long. */
export interface PadConfirmedResult {
  matchId: string;
  score: PadResultScore;
  /** Epoch ms the server accepted it. */
  at: number;
}

export type PadSubmissionState =
  | { status: 'idle' }
  /** In flight, or waiting to try again. The entered score stays on screen. */
  | { status: 'pending'; pending: PadPendingResult }
  /** Applied by the server, and being SHOWN as applied. See PAD_RESULT_CONFIRM_MS. */
  | { status: 'confirmed'; confirmed: PadConfirmedResult }
  /** The server answered and refused. The pad must re-read the court. */
  | { status: 'refused'; error: string };

export interface PadResultSubmission {
  state: PadSubmissionState;
  /** Queue a result. Replaces any pending one for the SAME match. */
  submit: (matchId: string, score: PadResultScore) => void;
  /** Try again now — the screen's button. The timer does this on its own. */
  retryNow: () => void;
  /** Drop a refusal or a confirmation — the screen's «Lukk», and the timer. */
  acknowledge: () => void;
}

/**
 * @param paired   whether this tablet is paired at all (#663: the credential
 *                 itself is an httpOnly cookie the transport carries)
 * @param onApplied called with the SERVER's court view, the only committed truth
 */
export function usePadResultSubmission(
  paired: boolean,
  onApplied: (view: Record<string, unknown>) => void,
  opts: { retryMs?: number; confirmMs?: number } = {},
): PadResultSubmission {
  const retryMs = opts.retryMs ?? PAD_RESULT_RETRY_MS;
  const confirmMs = opts.confirmMs ?? PAD_RESULT_CONFIRM_MS;
  /**
   * The queued submission. Its identity changes only when the OPERATOR submits
   * something new — never when an attempt fails.
   *
   * That separation is the whole design. An earlier version drove the attempt
   * effect off the full state, so recording a failed attempt changed the state,
   * which re-ran the effect, which attempted again — a retry loop with no delay
   * at all, hammering the weakest link in the building exactly when it was
   * already failing. Retries are driven ONLY by `attemptTick`.
   */
  const [submission, setSubmission] = useState<PadPendingResult | null>(null);
  const [attemptTick, setAttemptTick] = useState(0);
  const [refusal, setRefusal] = useState<string | null>(null);
  /** The last applied result, held only long enough to be read off the wall. */
  const [confirmed, setConfirmed] = useState<PadConfirmedResult | null>(null);
  /** Counted in a ref so recording an attempt cannot trigger another one. */
  const attemptsRef = useRef(0);
  const [attempts, setAttempts] = useState(0);
  // Two attempts for one submission would be harmless on the server — same
  // derived id — but would double the traffic from every pad in the hall.
  const inFlight = useRef(false);
  // Held in a ref so a new callback identity from the parent cannot re-submit.
  const appliedRef = useRef(onApplied);
  appliedRef.current = onApplied;

  const submit = useCallback((matchId: string, score: PadResultScore) => {
    attemptsRef.current = 0;
    setAttempts(0);
    setRefusal(null);
    // A new entry supersedes the previous confirmation — the pad must never
    // show «registrert» over a match that is being entered now.
    setConfirmed(null);
    setSubmission({
      matchId,
      score,
      requestId: padResultRequestId(matchId, score),
      attempts: 0,
      since: Date.now(),
    });
  }, []);

  const retryNow = useCallback(() => setAttemptTick((n) => n + 1), []);
  const acknowledge = useCallback(() => {
    setSubmission(null);
    setRefusal(null);
    setConfirmed(null);
  }, []);

  const requestId = submission?.requestId ?? null;

  // ONE attempt per (submission, tick).
  useEffect(() => {
    if (!submission || !paired || refusal || inFlight.current) return undefined;
    let cancelled = false;
    inFlight.current = true;

    void (async () => {
      const outcome = await submitPadResult(submission.matchId, submission.score);
      inFlight.current = false;
      attemptsRef.current += 1;
      if (cancelled) return;
      setAttempts(attemptsRef.current);

      if (outcome.status === 'applied') {
        // The server's view is the only thing rendered as committed.
        appliedRef.current(outcome.view);
        setSubmission(null);
        // SAY SO. The view underneath has already become whatever the court is
        // now — usually a plain booking, because the match is finished. Without
        // this the only feedback a success gives is the match vanishing.
        setConfirmed({ matchId: submission.matchId, score: submission.score, at: Date.now() });
        return;
      }
      if (outcome.status === 'refused') {
        // The server ANSWERED. Retrying cannot change its mind, and the pad
        // must show what is on the court rather than what was typed.
        setRefusal(outcome.error);
      }
      // Unreachable: the submission stays queued and the timer below tries
      // again. Nothing is recorded as committed.
    })();

    return () => {
      cancelled = true;
    };
    // `requestId` rather than `submission`: a new object for the same
    // submission must not mean another attempt.
  }, [requestId, paired, refusal, attemptTick, submission]);

  // Nobody is standing next to a wall-mounted pad, so the retry is automatic;
  // the button on the screen only shortcuts this timer.
  useEffect(() => {
    if (!submission || refusal) return undefined;
    const id = setTimeout(() => setAttemptTick((n) => n + 1), retryMs);
    return () => clearTimeout(id);
  }, [requestId, refusal, retryMs, attemptTick, submission]);

  // The confirmation clears itself: nobody is standing at a wall-mounted pad
  // to dismiss it, and a kiosk left showing a six-minute-old result is a lie.
  useEffect(() => {
    if (!confirmed) return undefined;
    const id = setTimeout(() => setConfirmed(null), confirmMs);
    return () => clearTimeout(id);
  }, [confirmed, confirmMs]);

  // ORDER MATTERS. A refusal outranks everything (it is the thing that must be
  // read), then work in flight, then the confirmation of work that is done.
  const state: PadSubmissionState = refusal
    ? { status: 'refused', error: refusal }
    : submission
      ? { status: 'pending', pending: { ...submission, attempts } }
      : confirmed
        ? { status: 'confirmed', confirmed }
        : { status: 'idle' };

  return { state, submit, retryNow, acknowledge };
}
